'use client';

import React, { useMemo, useId } from 'react';
import dynamic from 'next/dynamic';
import type { InputProps, GroupBase } from 'react-select';
import { components } from 'react-select';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { useCountries } from '@/contexts/CountriesContext';
import type { Country } from '@/contexts/CountriesContext';

const Select = dynamic(() => import('react-select'), { ssr: false });

export type { Country };

interface NationalitySelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  inputId?: string;
}

const NoAutoCompleteInput = (props: InputProps<any, boolean, GroupBase<any>>) => (
  <components.Input {...props} autoComplete="off" data-lpignore="true" data-form-type="other" />
);

interface SelectOption {
  value: string;
  label: string;
  code?: string;
}

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderColor: state.isFocused ? 'rgba(0,0,0,0.40)' : 'rgba(0,0,0,0.08)',
    borderRadius: '0.375rem',
    minHeight: '40px',
    boxShadow: 'none',
    fontSize: '0.875rem',
    '&:hover': { borderColor: 'rgba(0,0,0,0.15)' },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#E31E24' : state.isFocused ? '#fef2f2' : 'white',
    color: state.isSelected ? 'white' : '#4F545A',
    fontSize: '0.875rem',
    padding: '8px 12px',
  }),
  placeholder: (base: any) => ({ ...base, color: '#9CA3AF' }),
  singleValue: (base: any) => ({ ...base, color: '#4F545A' }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

export default function NationalitySelect({
  value,
  onChange,
  placeholder,
  label,
  className = '',
  disabled = false,
  required = false,
  inputId,
}: NationalitySelectProps) {
  const { getVariable } = useLanguageVariables();
  const countries = useCountries();
  const id = useId();
  const loading = false;

  const options = useMemo(() => {
    return countries.map((c) => ({
      value: c.code,
      label: c.name,
      code: c.code,
    }));
  }, [countries]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const c = countries.find((x) => x.code === value || x.name === value);
    return c ? { value: c.code, label: c.name } : value ? { value, label: value } : null;
  }, [value, countries]);

  const effectivePlaceholder = placeholder ?? getVariable('reservation.select_nationality', 'Select nationality');
  const effectiveLabel = label ?? getVariable('reservation.nationality', 'Nationality');

  return (
    <div className={className}>
      {effectiveLabel && (
        <label htmlFor={inputId || id} className="block text-sm font-medium text-alsero-dark-gray mb-1">
          {effectiveLabel}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <Select
        inputId={inputId || id}
        value={selectedOption}
        onChange={(opt) => onChange((opt as SelectOption | null)?.value ?? '')}
        options={options}
        placeholder={effectivePlaceholder}
        isDisabled={disabled}
        isLoading={loading}
        isSearchable
        isClearable
        styles={selectStyles}
        noOptionsMessage={() => getVariable('customer_panel.common.no_options', 'No options')}
        loadingMessage={() => getVariable('customer_panel.common.loading', 'Loading...')}
        components={{ Input: NoAutoCompleteInput }}
      />
    </div>
  );
}
